// src/components/AgentWakeupSettings.js
// « Qui suit les dossiers en attente » — top card of the Relances page. Who
// comes back to an e-mail, form, API, Envoi du Net or Spacefill case when the
// carrier or the customer does not answer:
// - off (« Relances automatiques »): the mail service's timers, at the send
//   hour, with the delays of the page's table; the agent schedules nothing.
// - pilot: the agent re-examines its cases (« Réveil IA ») and drafts; the
//   timers keep sending side by side.
// - live (« Agent IA »): the agent re-examines each waiting case at the
//   latest at the account's delays and judges whether to follow up; the
//   timers stop on every ticket, the ones already open included (KI-311,
//   Kevin 03/11 — MailMicroservice services/wakeup_gate.py); the follow-ups
//   they were running when the account goes live are handed over to the
//   agent as wake-ups (Backend agent_wakeup_service.seed_account).
// Switching mode always goes through a confirmation that says what changes.
// Also: what the AI does on a ticket assigned to the team (KI-211), the
// number of silent re-examinations after which the agent is told so and
// decides (KI-281: the platform never hands a ticket over), and — folded —
// the exact rules the agent receives (platform, read-only; account delays).
// API: GET/PUT /api/automation/ticket-workflow/wakeup {mode, max_consecutive,
// assigned_mode}; GET returns wakeup {mode, live_since…}, internal_rules,
// account_rules, bounds_hours, assigned {mode, effective, default}.
// `onModeChange(mode, data)` tells the page the mode (null when unreadable).

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlarmClock, Bot, ChevronRight, CircleCheck, Clock, FlaskConical, Lock } from 'lucide-react';
import { Badge, Banner, Card, Field, Modal } from '../design/ui';
import { formatShortDate } from '../utils/dates';
import './AgentWakeupSettings.css';

const API = '/api/automation/ticket-workflow/wakeup';
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });

export const WAKEUP_MODES = [
    ['off', Clock, 'Relances automatiques', 'Des minuteries relancent une fois par jour, à l’heure d’envoi, selon les délais ci-dessous. L’agent ne revient pas de lui-même sur les dossiers.'],
    ['pilot', FlaskConical, 'Pilote', 'L’agent revient sur les dossiers et prépare ses messages en brouillon, à valider par votre équipe. Les relances automatiques continuent en parallèle.'],
    ['live', Bot, 'Agent IA', 'L’agent revient lui-même sur chaque dossier en attente, au plus tard aux délais ci-dessous, et juge s’il faut relancer. Les relances automatiques s’arrêtent sur tous les tickets, y compris ceux déjà ouverts.'],
];
const STREAKS = [2, 3, 4, 5, 6, 8, 10];

// KI-211 — a ticket assigned to the team: [value, label, what it does].
export const ASSIGNED_MODES = [
    ['mute', 'l’IA se tait', 'L’IA ne traite pas le ticket ; la personne assignée est prévenue d’un message du client ou d’une réponse du transporteur.'],
    ['drafts', 'l’IA prépare des brouillons', 'L’IA prépare la réponse au client et ses demandes au transporteur en brouillon : rien ne part sans l’équipe. La personne assignée est prévenue.'],
    ['assist', 'l’IA assiste l’équipe (brouillons + actions transporteur)', 'La réponse au client reste un brouillon ; les demandes au transporteur suivent l’automatisation du client. Jamais de changement de statut, de remise ni de retour. La personne assignée est prévenue.'],
];

const dateOnly = (value) => formatShortDate(value);

const AgentWakeupSettings = ({ onModeChange }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [pending, setPending] = useState(null); // mode chosen, waiting for confirmation
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        try {
            const res = await axios.get(API, authHeaders());
            setData(res.data);
            onModeChange?.(res.data?.wakeup?.mode || 'off', res.data);
        } catch (e) {
            setError(t('agentWakeup.loadError', 'Impossible de charger le réveil de l’agent.'));
            onModeChange?.(null, null);
        }
    }, [t, onModeChange]);

    useEffect(() => { load(); }, [load]);

    const save = async (patch) => {
        setBusy(true);
        setError('');
        try {
            await axios.put(API, patch, authHeaders());
            setPending(null);
            await load();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : t('agentWakeup.saveError', 'Le réglage n’a pas pu être enregistré.'));
        } finally {
            setBusy(false);
        }
    };

    if (!data) {
        if (!error) {
            return <span className="u-skeleton wk-v2__skeleton" aria-hidden="true" />;
        }
        return (
            <Card icon={AlarmClock} title={t('agentWakeup.title', 'Qui suit les dossiers en attente')}>
                <Banner tone="danger">{error}</Banner>
            </Card>
        );
    }
    const config = data.wakeup || {};
    const mode = config.mode || 'off';
    const chosen = pending || mode;
    const assigned = data.assigned || null;
    const modeLabel = (key) => {
        const known = WAKEUP_MODES.find(([value]) => value === key);
        return known ? t(`agentWakeup.modes.${key}.label`, known[2]) : key;
    };
    const assignedLabel = (key) => {
        const known = ASSIGNED_MODES.find(([value]) => value === key);
        return known ? t(`agentWakeup.assigned.modes.${key}`, known[1]) : key;
    };
    const assignedHint = (key) => {
        const known = ASSIGNED_MODES.find(([value]) => value === key);
        return known ? t(`agentWakeup.assigned.hints.${key}`, known[2]) : '';
    };
    // An unknown stored value stays selectable (never silently replaced).
    const assignedOptions = assigned?.mode && !ASSIGNED_MODES.some(([value]) => value === assigned.mode)
        ? [...ASSIGNED_MODES.map(([value]) => value), assigned.mode]
        : ASSIGNED_MODES.map(([value]) => value);
    const liveSince = mode === 'live' && config.live_since ? dateOnly(config.live_since) : null;

    const confirmText = pending === 'live'
        ? t('agentWakeup.confirmLive', 'Tous les tickets en attente, y compris ceux déjà ouverts, seront suivis par l’agent : ses messages partiront selon vos règles d’automatisation et les relances automatiques s’arrêtent. Les relances en cours sont reprises par l’agent.')
        : pending === 'pilot'
            ? t('carriersUi.wakeup.confirmPilot', 'L’agent revient sur les dossiers et prépare ses messages en brouillon ; les relances automatiques continuent. Le changement s’applique aux prochains traitements.')
            : t('carriersUi.wakeup.confirmOff', 'L’agent ne revient plus de lui-même sur les dossiers : seules les relances automatiques suivent les dossiers en attente. Le changement s’applique aux prochains traitements.');

    return (
        <Card
            icon={AlarmClock}
            title={t('agentWakeup.title', 'Qui suit les dossiers en attente')}
            description={t('agentWakeup.subtitle', 'Sur l’e-mail, les formulaires, l’API de vos clients, Envoi du Net et Spacefill : qui revient sur un dossier quand le transporteur ou le client ne répond pas.')}
            className="wk-v2"
            flush
        >
            <div className="wk-v2__body">
                <p className="wk-v2__current" role="status">
                    <span>{t('agentWakeup.currentLabel', 'Sur votre compte :')}</span>{' '}
                    <strong>{modeLabel(mode)}</strong>
                    {liveSince && <span>{' '}{t('agentWakeup.since', 'depuis le {{date}}', { date: liveSince })}</span>}
                </p>

                <div className="wk-v2__modes" role="radiogroup" aria-label={t('agentWakeup.modeLabel', 'Qui suit les dossiers en attente')}>
                    {WAKEUP_MODES.map(([key, Icon, label, text]) => (
                        <button
                            key={key}
                            type="button"
                            role="radio"
                            aria-checked={chosen === key}
                            disabled={busy}
                            className={`wk-v2__mode${chosen === key ? ' is-on' : ''}`}
                            onClick={() => setPending(key === mode ? null : key)}
                        >
                            <span className="wk-v2__mode-top">
                                <span className="u-icon-chip u-icon-chip--sm" aria-hidden="true"><Icon strokeWidth={1.8} /></span>
                                <span className="wk-v2__mode-label">{t(`agentWakeup.modes.${key}.label`, label)}</span>
                                {key === mode && <Badge tone="accent" icon={CircleCheck}>{t('agentWakeup.current', 'actuel')}</Badge>}
                            </span>
                            <span className="wk-v2__mode-text">{t(`agentWakeup.modes.${key}.text`, text)}</span>
                        </button>
                    ))}
                </div>
                {error && !pending && <Banner tone="danger">{error}</Banner>}
            </div>

            {(assigned || config.enabled) && (
                <ul className="u-list wk-v2__settings">
                    {assigned && (
                        <li className="u-row">
                            <div className="u-row__main">
                                <Field
                                    label={t('agentWakeup.assigned.label', 'Ticket assigné à l’équipe :')}
                                    htmlFor="wk-assigned"
                                    help={`${assignedHint(assigned.effective)}${!assigned.mode && assigned.default === 'assist'
                                        ? ` ${t('agentWakeup.assigned.defaultLive', 'Par défaut parce que le réveil est en mode Agent IA.')}`
                                        : ''}`}
                                >
                                    <select
                                        id="wk-assigned"
                                        className="u-select wk-v2__select"
                                        value={assigned.mode || 'default'}
                                        disabled={busy}
                                        onChange={(e) => save({ assigned_mode: e.target.value })}
                                    >
                                        <option value="default">
                                            {t('agentWakeup.assigned.default', 'Par défaut ({{mode}})', { mode: assignedLabel(assigned.default) })}
                                        </option>
                                        {assignedOptions.map((value) => (
                                            <option key={value} value={value}>{assignedLabel(value)}</option>
                                        ))}
                                    </select>
                                </Field>
                            </div>
                        </li>
                    )}
                    {config.enabled && (
                        <li className="u-row">
                            <div className="u-row__main">
                                <Field
                                    label={t('agentWakeup.streakLabel', 'Réexamens d’affilée sans nouveauté avant d’avertir l’agent :')}
                                    htmlFor="wk-streak"
                                    help={t('agentWakeup.streakHint', 'Passé ce nombre, l’agent en est averti et décide lui-même : attendre, relancer, prévenir le client ou confier le ticket à l’équipe. La plateforme ne confie jamais un ticket à sa place.')}
                                >
                                    <select
                                        id="wk-streak"
                                        className="u-select wk-v2__select wk-v2__select--narrow"
                                        value={config.max_consecutive || 4}
                                        disabled={busy}
                                        onChange={(e) => save({ max_consecutive: Number(e.target.value) })}
                                    >
                                        {[...new Set([...STREAKS, config.max_consecutive || 4])].sort((a, b) => a - b).map((n) => (
                                            <option key={n} value={n}>{n}</option>
                                        ))}
                                    </select>
                                </Field>
                            </div>
                        </li>
                    )}
                </ul>
            )}

            <details className="u-disclosure wk-v2__details">
                <summary>
                    <ChevronRight aria-hidden="true" />
                    {t('agentWakeup.detailsSummary', 'Les règles exactes que reçoit l’agent')}
                </summary>
                <div className="wk-v2__rules">
                    <div className="wk-v2__rules-col">
                        <h3 className="wk-v2__rules-title"><Lock aria-hidden="true" /> {t('agentWakeup.internalTitle', 'Règles de la plateforme')}</h3>
                        <p className="u-help">{t('agentWakeup.internalHint', 'Toujours appliquées, non modifiables : elles gardent l’agent sur les rails.')}</p>
                        <ul>
                            {(data.internal_rules || '').split('\n').filter((line) => line.startsWith('- ')).map((line) => (
                                <li key={line}>{line.slice(2)}</li>
                            ))}
                        </ul>
                    </div>
                    <div className="wk-v2__rules-col">
                        <h3 className="wk-v2__rules-title">{t('agentWakeup.accountTitle', 'Vos délais, tels que l’agent les reçoit')}</h3>
                        <p className="u-help">{t('agentWakeup.accountHintAgent', 'Ils viennent du tableau des délais ci-dessous. L’heure d’envoi ne s’applique pas à l’agent : il choisit le moment d’après la chronologie du dossier, la nuit et le week-end.')}</p>
                        <ul>
                            {(data.account_rules || []).map((line) => <li key={line}>{line}</li>)}
                        </ul>
                        <p className="u-help">
                            {t('agentWakeup.perClient', 'Pour un client B2B ou un transporteur en particulier, écrivez la consigne sur sa fiche — l’agent la lit pour ses dossiers :')}{' '}
                            <Link className="u-link" to="/clients-instructions">{t('agentWakeup.clients', 'clients B2B')}</Link>,{' '}
                            <Link className="u-link" to="/carriers-instructions">{t('agentWakeup.carriers', 'transporteurs')}</Link>.
                        </p>
                    </div>
                </div>
            </details>

            {pending && (
                <Modal
                    icon={WAKEUP_MODES.find(([value]) => value === pending)?.[1] || AlarmClock}
                    title={t('carriersUi.wakeup.confirmTitle', 'Passer en mode « {{mode}} » ?', { mode: modeLabel(pending) })}
                    description={confirmText}
                    onClose={() => setPending(null)}
                    closeLabel={t('common.close', 'Fermer')}
                    footer={(
                        <>
                            <button type="button" className="u-btn u-btn--secondary" disabled={busy} onClick={() => setPending(null)}>
                                {t('common.cancel', 'Annuler')}
                            </button>
                            <button type="button" className="u-btn u-btn--primary" disabled={busy} onClick={() => save({ mode: pending })}>
                                {busy ? t('common.saving', 'Enregistrement…') : t('agentWakeup.apply', 'Appliquer')}
                            </button>
                        </>
                    )}
                >
                    {error && <Banner tone="danger">{error}</Banner>}
                </Modal>
            )}
        </Card>
    );
};

export default AgentWakeupSettings;
